/**
 * Browser media for the built runtime (spec 012): one URL resolver serves
 * both the images `SceneRuntime` draws and the sounds its audio port plays.
 */

import type { PlayableScene } from '../@types';
import type { AudioPlayRequest, AudioPort } from './audioCues';
import type { SceneImage, SceneImages } from './SceneRuntime';

/** Where the media with this id is served (a published package, the API, …). */
export type MediaUrlResolver = (mediaId: number) => string;

const clampGain = (g: number) => (Number.isFinite(g) ? Math.min(1, Math.max(0, g)) : 1);

/** Every image a scene can draw: sprite state images, the background's base image (spec 026), its states' pictures (spec 039) and its layers. */
export function sceneImageIds(scene: Pick<PlayableScene, 'sprites' | 'background'>): number[] {
  const ids = new Set<number>();
  const base = scene.background?.baseMediaId;
  if (typeof base === 'number' && base > 0) ids.add(base);
  // Every background state's picture (spec 039), so a change never waits on a load.
  for (const state of Array.isArray(scene.background?.states) ? scene.background!.states! : []) {
    if (typeof state?.mediaId === 'number' && state.mediaId > 0) ids.add(state.mediaId);
  }
  for (const sprite of scene.sprites ?? []) {
    for (const state of sprite.states ?? []) if (state.mediaId > 0) ids.add(state.mediaId);
  }
  for (const layer of scene.background?.layers ?? []) if (layer.mediaId > 0) ids.add(layer.mediaId);
  return Array.from(ids);
}

/**
 * Whether an image at `url` is requested with CORS (`crossOrigin`). A
 * `file://` image never is (spec 011): browsers and the Android and iOS web
 * views refuse a CORS load of a file URL, so the extracted bundle's media
 * would not draw. Everything else keeps `crossOrigin = 'anonymous'`.
 */
export function corsImageUrl(url: string, base: string | undefined = typeof document !== 'undefined' ? document.baseURI : undefined): boolean {
  try {
    return new URL(url, base).protocol !== 'file:';
  } catch {
    return true;
  }
}

/**
 * Load one image and decode it before resolving, so the first `drawImage`
 * does not decode it on the main thread mid-animation (WebKit decodes an
 * `<img>` lazily at its first draw). Resolves `null` when it fails to load
 * (a missing image is not drawn); a failed or absent `decode()` (jsdom, old
 * browsers) still resolves the loaded image.
 */
export function loadImage(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    if (corsImageUrl(url)) img.crossOrigin = 'anonymous';
    img.onload = () => {
      void decodeImage(img).then(() => resolve(img));
    };
    img.onerror = () => {
      console.warn('[StoryMotion] image failed to load:', url);
      resolve(null);
    };
    img.src = url;
  });
}

/** `img.decode()` where the browser has it; never rejects. */
export async function decodeImage(img: HTMLImageElement): Promise<void> {
  if (typeof img.decode !== 'function') return;
  try {
    await img.decode();
  } catch {
    // A decode failure after a successful load still draws (the browser decodes at draw time).
  }
}

/**
 * The loaded image as an `ImageBitmap` (decoded pixels the browser keeps
 * while the bitmap lives, unlike an `<img>`'s decoded data, which WebKit may
 * drop and decode again at a later draw). Without `createImageBitmap`, or
 * when it fails, the image itself.
 */
export async function toDrawableImage(img: HTMLImageElement): Promise<SceneImage> {
  if (typeof createImageBitmap !== 'function') return img;
  try {
    return await createImageBitmap(img);
  } catch {
    return img;
  }
}

/** Free a bitmap made by `toDrawableImage` (an `<img>` needs nothing). */
export function releaseDrawableImage(img: SceneImage | null | undefined): void {
  const close = (img as Partial<ImageBitmap> | null | undefined)?.close;
  if (typeof close === 'function') close.call(img);
}

/** Decode every image of a scene, keyed by media id; failures are left out. */
export async function loadSceneImages(
  scene: Pick<PlayableScene, 'sprites' | 'background'>,
  resolveUrl: MediaUrlResolver,
): Promise<SceneImages> {
  const images: SceneImages = {};
  await Promise.all(sceneImageIds(scene).map(async (id) => {
    const img = await loadImage(resolveUrl(id));
    if (img) images[id] = img;
  }));
  return images;
}

/**
 * An audio port (spec 007) with one `HTMLAudioElement` per cue. Pan is not
 * applied (an audio element has none); a blocked autoplay is ignored.
 */
export function createBrowserAudioPort(resolveUrl: MediaUrlResolver): AudioPort<HTMLAudioElement> {
  return {
    play(cue: AudioPlayRequest): HTMLAudioElement {
      const audio = new Audio(resolveUrl(cue.mediaId));
      audio.loop = cue.loop;
      audio.volume = clampGain(cue.initialGain);
      if (cue.offsetMs > 0) {
        const seek = () => {
          const offset = cue.offsetMs / 1000;
          audio.currentTime = cue.loop && audio.duration > 0 ? offset % audio.duration : offset;
        };
        if (audio.readyState >= 1) seek();
        else audio.addEventListener('loadedmetadata', seek, { once: true });
      }
      const started = audio.play();
      if (started && typeof started.catch === 'function') started.catch(() => undefined);
      return audio;
    },
    stop(audio: HTMLAudioElement): void {
      audio.pause();
      audio.currentTime = 0;
    },
    setGain(audio: HTMLAudioElement, gain: number): void {
      audio.volume = clampGain(gain);
    },
  };
}
